import axios, { AxiosResponse, AxiosError } from "axios";
import { getCookie } from "@util/cookie";

const apiURL = import.meta.env.VITE_TALENT_AI_BE_BASE_URL;

/**
 * Bug report request interface
 */
/**
 * Plain serializable shape the BE actually receives.
 *
 * The DOM `MediaDeviceInfo` exposes its fields via non-enumerable getters on
 * the prototype, so `JSON.stringify(device)` collapses to `{}` and the wire
 * payload silently drops the data. Callers must copy the four fields onto a
 * POJO before assigning to `meta.audio_device` / `meta.video_device`.
 */
export interface BugReportDevice {
  deviceId: string;
  kind: MediaDeviceKind;
  label: string;
  groupId: string;
}

/** Where the candidate triggered the bug report from. */
export type BugReportType = "waiting-room" | "live-session";

export interface BugReportRequest {
  bug_description: string;
  bug_type: string;
  assessment_id?: string;
  meta: {
    user_id: string;
    /** Surfaces the trigger context to the BE so reports can be filtered. */
    type?: BugReportType;
    os?: string;
    browser?: string;
    browser_version?: string;
    cpu_architecture?: string;
    device_model?: string;
    device_vendor?: string;
    device_type?: string;
    audio_device?: BugReportDevice;
    video_device?: BugReportDevice;
    /** @deprecated kept for backward-compat; populated only by older callers */
    device?: string;
    /** @deprecated kept for backward-compat; populated only by older callers */
    app_version?: string;
  }
}

/**
 * Standard API response interface
 */
export interface BugReportResponse {
  success: boolean;
  status: number;
  message?: string;
  data?: any;
  error?: Error | AxiosError;
}

/**
 * Submits a bug report to the server
 * @param bugData - The bug report data
 * @returns Promise with the response data
 */
export const makeBugReport = async (bugData: BugReportRequest): Promise<BugReportResponse> => {
  try {
    const token = getCookie("authToken") || getCookie("tempAuthToken");

    if (!token) {
      return {
        success: false,
        status: 401,
        message: "No authentication token found",
      };
    }

    const response: AxiosResponse = await axios({
      url: `${apiURL}/report/bugs`,
      method: "POST",
      headers: {
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json",
      },
      data: bugData,
      timeout: 10000, // 10 second timeout
    });

    return {
      success: true,
      status: response.status,
      data: response.data,
      message: "Bug report submitted successfully",
    };
  } catch (error) {
    const err = error as AxiosError;
    const responseData = err.response?.data as Record<string, unknown>;

    return {
      success: false,
      status: err.response?.status || 500,
      message:
        (responseData?.message as string) ||
        (err.code === "ECONNABORTED"
          ? "Request timed out"
          : "Failed to submit bug report"),
      error: err,
    };
  }
};
